import { useNavigate } from 'react-router-dom'
import { User, Pencil, Trash2 } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { Tooltip } from '@/components/ui/Tooltip'
import { InfoPopover } from '@/components/ui/InfoPopover'
import type { UserGridProps, UserListItem } from '../types'
import {
  USER_MANAGEMENT_POLICIES,
} from '../constants'
import { formatUserEmail, formatPhoneForDisplay, isUserListRowLibertyAllRoles, formatDateTime, categorizeUserRoles } from '../utils/userManagementFormatters'
import { cn } from '@/lib/utils/helpers'

function StatusDot({ status }: { status: string | null }) {
  const s = status ?? 'Active'
  const colorMap: Record<string, string> = {
    Active: 'bg-emerald-500',
    Inactive: 'bg-rose-500',
    Locked: 'bg-amber-500',
  }
  return (
    <span className={cn('inline-block h-2 w-2 rounded-full mr-1.5', colorMap[s] ?? 'bg-slate-400')} />
  )
}

const selectCheckboxClass =
  'rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] h-4 w-4'

export function UserGrid({
  users,
  selectedIds,
  onSelectOne,
  onSelectAll,
  onImpersonate,
  onDelete,
}: UserGridProps) {
  const navigate = useNavigate()
  const { hasPolicy } = useUserRole()

  const canSelectAll = hasPolicy([USER_MANAGEMENT_POLICIES.SELECT_ALL])
  const canSelectUser = hasPolicy([USER_MANAGEMENT_POLICIES.SELECT_USER])
  const canEdit = hasPolicy([USER_MANAGEMENT_POLICIES.EDIT_USER])
  const canDelete = hasPolicy([USER_MANAGEMENT_POLICIES.DELETE_USER])
  const canImpersonateApplicant = hasPolicy([USER_MANAGEMENT_POLICIES.IMPERSONATE_APPLICANT])
  const canImpersonatePmc = hasPolicy([USER_MANAGEMENT_POLICIES.IMPERSONATE_PMC])



  const canImpersonateUser = (user: UserListItem): boolean => {
    const roles = user.rolename ?? []
    if (roles.length !== 1 || !user.role_id || isUserListRowLibertyAllRoles(user)) return false
    const rn = roles[0]?.rolename ?? ''
    if (rn === 'Applicant' || rn === 'Resident') {
      return canImpersonateApplicant
    }
    return canImpersonatePmc
  }

  const canDeleteUser = (_user: UserListItem): boolean => {
    return canDelete
  }

  const canSelectUserRow = (_user: UserListItem): boolean => {
    return canSelectUser
  }

  if (users.length === 0) {
    return (
      <div
        className="flex min-h-[280px] items-center justify-center p-8 text-center"
        data-testid="um-grid-empty"
      >
        <p className="text-lg font-semibold text-slate-500">No user found</p>
      </div>
    )
  }

  return (
    <div className="p-4" data-testid="um-grid">
      {canSelectAll && users.length > 0 && (
        <div className="flex items-center gap-2 pb-3 mb-3 border-b border-slate-100">
          <input
            type="checkbox"
            className={selectCheckboxClass}
            checked={
              users.length > 0 && users.every((u) => selectedIds.includes(u.user_id))
            }
            onChange={(e) => onSelectAll(e.target.checked)}
            aria-label="Select all users"
            data-testid="um-grid-select-all-checkbox"
          />
          <span className="text-sm font-medium text-slate-700">Select all</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {users.map((user) => (
        <div
          key={user.user_id}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
          data-testid={`um-grid-card-${user.user_id}`}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              {canSelectUser && (
                <div className="shrink-0 pt-0.5">
                  {canSelectUserRow(user) ? (
                    <input
                      type="checkbox"
                      className={selectCheckboxClass}
                      checked={selectedIds.includes(user.user_id)}
                      onChange={(e) => onSelectOne(user.user_id, e.target.checked)}
                      aria-label={`Select ${user.name}`}
                      data-testid={`um-grid-select-checkbox-${user.user_id}`}
                    />
                  ) : (
                    <span className="inline-block h-4 w-4" aria-hidden />
                  )}
                </div>
              )}
              <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 text-sm" data-testid={`um-grid-name-${user.user_id}`}>{user.name}</p>
              <p className="text-xs text-slate-500 mt-0.5" data-testid={`um-grid-email-${user.user_id}`}>{formatUserEmail(user.email) || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0" data-testid={`um-grid-status-${user.user_id}`}>
              <StatusDot status={user.account_status} />
              <span className="text-xs text-slate-600">{user.account_status ?? 'Active'}</span>
            </div>
          </div>

          <div className="space-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-400 w-20 shrink-0">Contact</span>
              <span data-testid={`um-grid-contact-${user.user_id}`}>{formatPhoneForDisplay(user.mobile_no) || '—'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-medium text-slate-400 w-20 shrink-0 mt-0.5">Role</span>
              <div className="flex flex-wrap gap-1" data-testid={`um-grid-roles-${user.user_id}`}>
                {(() => {
                  const roles = (user.rolename ?? [])
                    .map((r) => r.rolename?.trim())
                    .filter((name): name is string => !!name)

                  if (roles.length === 0) {
                    return <span className="text-slate-400">—</span>
                  }
                  if (roles.length === 1) {
                    return (
                      <span className="text-sm font-medium text-slate-700">
                        {roles[0]}
                      </span>
                    )
                  }

                  const categorized = categorizeUserRoles(roles)

                  return (
                    <InfoPopover
                      align="start"
                      maxWidth={320}
                      testId={`um-grid-roles-multiple-popover-${user.user_id}`}
                      content={
                        <div className="space-y-3 p-1 text-xs">
                          {categorized.map((group) => (
                            <div key={group.category}>
                              <div className="font-bold text-slate-800 border-b border-slate-100 pb-1 mb-1">
                                {group.category}
                              </div>
                              <ul className="space-y-1 pl-1 text-slate-700">
                                {group.roles.map((name) => (
                                  <li key={name} className="list-disc list-inside leading-snug">
                                    {name}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <button
                        type="button"
                        data-testid={`um-grid-roles-multiple-${user.user_id}`}
                        className="cursor-pointer select-none text-sm font-medium text-[rgb(21_151_255)] underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        Multiple Roles…
                      </button>
                    </InfoPopover>
                  )
                })()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-400 w-20 shrink-0">Last Access</span>
              <span data-testid={`um-grid-last-active-${user.user_id}`}>{formatDateTime(user.last_active) || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-400 w-20 shrink-0">Property</span>
              <span data-testid={`um-grid-property-${user.user_id}`}>{user.property_name || '—'}</span>
            </div>
          </div>

          {(canEdit || canDelete || canImpersonateApplicant || canImpersonatePmc) && (
            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
              {(canImpersonateApplicant || canImpersonatePmc) && (
                <div className="flex h-7 w-7 items-center justify-center shrink-0">
                  {canImpersonateUser(user) ? (
                    <Tooltip content="Impersonate">
                      <button
                        type="button"
                        onClick={() => onImpersonate(user.role_id)}
                        className="rounded-lg p-1.5 text-slate-500 transition-all hover:bg-[rgb(21_151_255)]/10 hover:text-[rgb(21_151_255)]"
                        aria-label="Impersonate"
                        data-testid={`um-grid-impersonate-btn-${user.user_id}`}
                      >
                        <User className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  ) : (
                    <span className="h-7 w-7" aria-hidden />
                  )}
                </div>
              )}
              {canEdit && (
                <div className="flex h-7 w-7 items-center justify-center shrink-0">
                  <Tooltip content="Edit">
                    <button
                      type="button"
                      onClick={() => navigate(`/user-management/edit/${user.user_id}`)}
                      className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                      aria-label="Edit"
                      data-testid={`um-grid-edit-btn-${user.user_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
              )}
              {canDelete && (
                <div className="flex h-7 w-7 items-center justify-center shrink-0">
                  {canDeleteUser(user) ? (
                    <Tooltip content="Delete">
                      <button
                        type="button"
                        onClick={() => onDelete(user)}
                        className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-all"
                        aria-label="Delete"
                        data-testid={`um-grid-delete-btn-${user.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  ) : (
                    <span className="h-7 w-7" aria-hidden />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      </div>
    </div>
  )
}
